import React, { useEffect, useRef } from 'react';
import ScrollReveal from './ScrollReveal';

const stats = [
  { value: 24, suffix: '/7', label: 'Edge Processing Uptime', desc: 'Continuous local analytics even during outages' },
  { value: 0, suffix: '', label: 'Raw Video in the Cloud', desc: 'All processing on-site. Only structured KPIs transmitted' },
  { value: 1, suffix: ' day', label: 'Typical Deployment Time', desc: 'Single-site setup including hardware and dashboards' },
  { value: 100, suffix: '%', label: 'Camera Compatibility', desc: 'Works with any existing IP or analog camera system' },
];

function AnimatedStat({ value, suffix }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const target = value;
    const duration = 1400;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      if (ref.current) ref.current.textContent = Math.round(eased * target);
      if (progress < 1) requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { requestAnimationFrame(tick); observer.disconnect(); }
    }, { threshold: 0.5 });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span className="proof-stat-value">
      <span ref={ref}>0</span>{suffix}
    </span>
  );
}

export default function SocialProof() {
  return (
    <section className="section proof-section" id="proof">
      <div className="section-inner">
        <ScrollReveal>
          <div className="proof-header">
            <div className="section-label">Proven in the Field</div>
            <h2 className="section-title">
              Deployed where <strong>reliability is non-negotiable</strong>
            </h2>
            <p className="section-subtitle">
              Built for intermittent power, unstable internet, and remote regions &mdash; operational
              across healthcare facilities in East Africa on solar, satellite, and generator infrastructure.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal>
          <div className="proof-stats">
            {stats.map((s) => (
              <div className="proof-stat-card" key={s.label}>
                <AnimatedStat value={s.value} suffix={s.suffix} />
                <span className="proof-stat-label">{s.label}</span>
                <span className="proof-stat-desc">{s.desc}</span>
              </div>
            ))}
          </div>
        </ScrollReveal>

        <ScrollReveal>
          <div className="proof-environments">
            <div className="proof-env-label">Validated Environments</div>
            <div className="proof-env-tags">
              <span className="proof-env-tag">Hospitals &amp; Clinics</span>
              <span className="proof-env-tag">Schools &amp; Education</span>

              <span className="proof-env-tag">Distribution Centers</span>
              <span className="proof-env-tag">Government Facilities</span>
              <span className="proof-env-tag">Remote &amp; Off-Grid Sites</span>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
